import { WarningCircleIcon } from '@phosphor-icons/react';
import { type VariantProps, cva } from 'class-variance-authority';
import React from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

function Empty({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty"
      className={cn(
        'flex min-w-0 flex-1 flex-col items-center justify-center gap-6 text-balance rounded-lg border-dashed p-6 text-center md:p-12',
        className,
      )}
      {...props}
    />
  );
}

function EmptyHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-header"
      className={cn('flex max-w-sm flex-col items-center gap-2 text-center', className)}
      {...props}
    />
  );
}

/**
 * `icon` and `illustration` are NOT interchangeable, and the difference is a
 * measured one rather than a preference.
 *
 * `icon` is a 40px muted tile whose `[&_svg:not([class*='size-'])]:size-6` rule
 * forces its child to 24px. That is correct for a Phosphor glyph and wrong for
 * a house LineIcon: the 26 drawings in `@/components/ui/illustrations` carry
 * variable stroke weights (2.0 / 1.5 / 1.0) and a warm accent blob that only
 * resolve in the ~80px band. Measured live 2026-08-27 on `/briefs`, a LineIcon
 * written as `h-12 w-auto` inside `variant="icon"` rendered at **24x24** —
 * the `size-6` rule silently beat the height class — i.e. an illegible grey
 * smudge in a grey chip. That is the same defect the founder named on
 * 2026-08-19 ("what does that random graph icon there do?"), recorded in
 * `AnalyticsCardEmpty`'s docblock.
 *
 * `illustration` therefore imposes no tile and no size, so the drawing keeps
 * the height the caller asks for. Card-body empties still get no drawing at
 * all — see `AnalyticsCardEmpty`, which is the one empty state for that band.
 */
const emptyMediaVariants = cva(
  'mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        icon: "bg-muted text-foreground flex size-10 shrink-0 items-center justify-center rounded-lg [&_svg:not([class*='size-'])]:size-6",
        illustration: 'bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function EmptyMedia({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof emptyMediaVariants>) {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  );
}

function EmptyTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-title"
      className={cn('text-lg font-medium tracking-tight', className)}
      {...props}
    />
  );
}

function EmptyDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <div
      data-slot="empty-description"
      className={cn(
        'text-muted-foreground [&>a:hover]:text-primary text-sm/relaxed [&>a]:underline [&>a]:underline-offset-4',
        className,
      )}
      {...props}
    />
  );
}

function EmptyContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-content"
      className={cn(
        'flex w-full min-w-0 max-w-sm flex-col items-center gap-4 text-balance text-sm',
        className,
      )}
      {...props}
    />
  );
}

/**
 * One band for every house illustration, everywhere: 80px tall, width from the
 * drawing's own aspect ratio. It is the size the published contract at
 * `/style-guide/illustrations` already documents, and `w-auto` matters because
 * no LineIcon is square (`viewBox` 80x56, 120x80 or 120x120) — a square `size-*`
 * letterboxes them to different heights per drawing, so a page of empties stops
 * reading as one set.
 */
const EMPTY_ILLUSTRATION = 'h-20 w-auto';

function EmptyIllustration({ as: Illustration }: { as: React.FC<{ className?: string }> }) {
  return (
    // Decorative: the title and description carry every bit of the meaning, and
    // none of the 27 drawings ships a <title>. Hiding it keeps a screen reader
    // from announcing an unnamed graphic before the sentence that matters.
    <EmptyMedia variant="illustration" aria-hidden="true">
      <Illustration className={EMPTY_ILLUSTRATION} />
    </EmptyMedia>
  );
}

interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  variant?: 'default' | 'outline' | 'neutral' | 'flat';
  icon?: React.ElementType;
}

interface EmptyStateProps {
  /**
   * A house LineIcon from `@/components/ui/illustrations`. Rendered at 80px on
   * the full-surface variants (`page`, `default`, `card`) and deliberately NOT
   * on `compact`, which is a card-body band. Wins over `icon` when both are given.
   */
  illustration?: React.FC<{ className?: string }>;
  /** A Phosphor glyph, drawn at 24px in a 40px muted tile. */
  icon?: React.ElementType;
  title: string;
  description?: string;
  variant?: 'default' | 'card' | 'inline' | 'compact' | 'minimal' | 'error' | 'page';
  actions?: EmptyStateAction[];
  className?: string;
  children?: React.ReactNode;
  retry?: () => void;
}

function EmptyState({
  illustration: Illustration,
  icon: Icon,
  title,
  description,
  variant = 'default',
  actions,
  className,
  children,
  retry,
}: EmptyStateProps) {
  if (variant === 'page') {
    return (
      <Empty className={cn('border-0', className)}>
        <EmptyHeader>
          {Illustration && <EmptyIllustration as={Illustration} />}
          <EmptyTitle>{title}</EmptyTitle>
          {description && <EmptyDescription>{description}</EmptyDescription>}
        </EmptyHeader>
        {(actions || children) && (
          <EmptyContent>
            {actions && (
              <div className="flex items-center gap-2">
                {actions.map((action) => (
                  <Button
                    key={action.label}
                    variant={action.variant || 'outline'}
                    size="sm"
                    onClick={action.onClick}
                    className="gap-1.5"
                  >
                    {action.icon && <action.icon className="size-3.5" />}
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
            {children}
          </EmptyContent>
        )}
      </Empty>
    );
  }
  if (variant === 'error') {
    return (
      <Empty
        className={cn('border border-border/60 bg-muted/30 px-5 py-8 gap-3 md:p-8', className)}
      >
        <EmptyHeader className="gap-2">
          <EmptyMedia>
            <WarningCircleIcon className="size-8 text-muted-foreground/60" weight="regular" />
          </EmptyMedia>
          <EmptyTitle className="text-sm">{title}</EmptyTitle>
          {description && (
            <EmptyDescription className="text-sm text-muted-foreground max-w-sm">
              {description}
            </EmptyDescription>
          )}
        </EmptyHeader>
        {(retry || children) && (
          <EmptyContent>
            {retry && (
              <Button variant="outline" size="sm" onClick={retry}>
                Try again
              </Button>
            )}
            {children}
          </EmptyContent>
        )}
      </Empty>
    );
  }

  if (variant === 'inline') {
    return (
      <p className={cn('text-caption text-muted-foreground/50 py-6 text-center', className)}>
        {title}
      </p>
    );
  }

  if (variant === 'minimal') {
    return (
      <div
        className={cn('flex flex-col items-center justify-center px-4 py-6 text-center', className)}
      >
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description && (
          <p className="mt-1 max-w-sm text-balance text-detail text-muted-foreground">
            {description}
          </p>
        )}
      </div>
    );
  }

  const isCompact = variant === 'compact';
  const isCard = variant === 'card';
  const content = (
    <Empty
      className={cn(
        'border-0 flex-none',
        isCompact && 'p-3 md:p-4 gap-2',
        // `card` puts the caller's className on the Card below — the outermost
        // element it actually sees — so `max-w-*` bounds the card rather than
        // a child of a full-bleed one. Until 2026-08-27 it landed here, which
        // is why `/products` rendered a 1231px dashed card with its content
        // pinned left of centre while `border-border/60 bg-card/40` did nothing.
        !isCard && className,
      )}
    >
      <EmptyHeader className={cn(isCompact && 'gap-2')}>
        {/* Compact is a card-body / nested band, and a house drawing shrunk into
            it is the "random graph icon" the founder rejected on 2026-08-19.
            It gets a Phosphor glyph in the icon tile or nothing at all. */}
        {Illustration && !isCompact ? (
          <EmptyIllustration as={Illustration} />
        ) : Icon ? (
          <EmptyMedia variant="icon">
            <Icon />
          </EmptyMedia>
        ) : null}
        <EmptyTitle className={cn(isCompact && 'text-sm')}>{title}</EmptyTitle>
        {description && (
          <EmptyDescription className={cn(isCompact && 'text-caption')}>
            {description}
          </EmptyDescription>
        )}
      </EmptyHeader>
      {(actions || children) && (
        <EmptyContent>
          {actions && (
            <div className="flex items-center gap-2">
              {actions.map((action) => (
                <Button
                  key={action.label}
                  variant={action.variant || 'outline'}
                  size="sm"
                  onClick={action.onClick}
                  className="gap-1.5"
                >
                  {action.icon && <action.icon className="size-3.5" />}
                  {action.label}
                </Button>
              ))}
            </div>
          )}
          {children}
        </EmptyContent>
      )}
    </Empty>
  );

  if (isCard) {
    return (
      <Card className={cn('w-full border-dashed bg-card/50 backdrop-blur-sm', className)}>
        {content}
      </Card>
    );
  }

  return content;
}

export { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent, EmptyMedia, EmptyState };
export type { EmptyStateProps, EmptyStateAction };
